import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, SCHEDULER_URL } from "../../data/content";

export const Navbar = ({ onBookCall }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="navbar"
    >
      <div className="container-x">
        <div
          className={`mt-4 flex items-center justify-between rounded-2xl px-4 md:px-5 h-16 transition-colors duration-300 ${
            scrolled ? "glass shadow-[0_8px_32px_rgba(0,0,0,0.35)]" : "bg-white/[0.015] border border-white/5"
          }`}
        >
          <button onClick={() => scrollTo("#hero")} data-testid="nav-logo" className="flex items-center">
            <Logo />
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-muted-foreground hover:text-white transition-colors duration-200"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={onBookCall}
              data-testid="nav-book-call"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-xl bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2.5 transition-colors duration-200"
            >
              Book a Call <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/5 border border-white/10 text-white"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="md:hidden mt-2 glass rounded-2xl p-4"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.href}
                    onClick={() => scrollTo(l.href)}
                    className="text-left py-3 text-base text-muted-foreground hover:text-white border-b border-white/5 last:border-0"
                  >
                    {l.label}
                  </button>
                ))}
                <button
                  onClick={() => { setOpen(false); onBookCall(); }}
                  className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-brand text-white text-sm font-medium px-4 py-3"
                >
                  Book a Discovery Call <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;
